<template>
  <div class="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-p-4">
    <!-- Enhanced Input Area -->
    <div class="tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-focus-within:tw-ring-2 tw-focus-within:tw-ring-primary-500 tw-focus-within:tw-border-primary-500 tw-transition-all">
      <!-- Textarea -->
      <textarea 
        v-model="messageText"
        @keydown.enter.prevent="sendMessage"
        @input="handleInput"
        placeholder="Type your message..."
        class="tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-border-0 tw-rounded-lg tw-resize-none tw-outline-none tw-bg-transparent tw-min-h-[10px] tw-max-h-[120px]"
        rows="1"
        ref="messageInput"
        :disabled="sending"
      ></textarea>
      
      <!-- Input Actions -->
      <div class="tw-flex tw-items-center tw-justify-end tw-px-3 tw-pb-2">
        <!-- Right Actions -->
        <div class="tw-flex tw-items-center tw-gap-1">
          <button 
            v-if="messageText.trim()"
            @click="clearMessage"
            class="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded-md tw-transition-all tw-duration-200 tw-group"
            title="Clear message"
            :disabled="sending"
          >
            <icon icon="solar:close-circle-bold-duotone" class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" />
          </button>
          <button 
            @click="sendMessage"
            :disabled="!messageText.trim() || sending"
            class="tw-p-2 tw-bg-primary-500 tw-text-white tw-rounded-md hover:tw-bg-primary-600 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-flex-shrink-0 tw-group tw-shadow-sm hover:tw-shadow-md"
            title="Send message"
          >
            <icon 
              v-if="!sending"
              :icon="messageText.trim() ? 'solar:map-arrow-right-bold-duotone' : 'solar:map-arrow-right-linear'" 
              class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" 
            />
            <icon 
              v-else
              icon="line-md:loading-alt-loop" 
              class="tw-text-sm" 
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Quick Reply Suggestions -->
    <div v-if="!messageText.trim() && !sending" class="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-flex-wrap">
      <button 
        v-for="suggestion in quickReplies" 
        :key="suggestion"
        @click="useQuickReply(suggestion)"
        class="tw-px-3 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-transition-all tw-duration-200 tw-text-gray-600 hover:tw-text-primary-700"
      >
        {{ suggestion }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, nextTick, defineExpose, onUnmounted } from 'vue';

// Props
const props = defineProps({
  conversationId: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

// Import real-time messaging service
import realtimeMessagingService from '@/services/realtime-messaging';

// Emits
const emit = defineEmits(['message-sent', 'message-sending']);

// Reactive data
const messageText = ref('');
const sending = ref(false);
const messageInput = ref(null);
const typingTimeout = ref(null);

// Quick reply suggestions
const quickReplies = ref([
  'Hello!',
  'How are you?',
  'Thank you!',
  'I\'m interested',
  'Can we schedule a meeting?'
]);

// Auto-resize textarea
const handleInput = () => {
  if (messageInput.value) {
    messageInput.value.style.height = 'auto';
    messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
  }

  // Send typing indicator
  if (messageText.value.trim()) {
    realtimeMessagingService.sendTypingIndicator(props.conversationId, true);
    
    // Clear existing timeout
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value);
    }
    
    // Set timeout to stop typing indicator after 2 seconds
    typingTimeout.value = setTimeout(() => {
      realtimeMessagingService.sendTypingIndicator(props.conversationId, false);
    }, 2000);
  }
};

// Clear message
const clearMessage = () => {
  messageText.value = '';
  if (messageInput.value) {
    messageInput.value.style.height = 'auto';
  }
};

// Use quick reply
const useQuickReply = (reply) => {
  messageText.value = reply;
  handleInput();
  messageInput.value?.focus();
};

// Send message function
const sendMessage = async () => {
  if (!messageText.value.trim() || sending.value || props.disabled) return;
  
  const messageContent = messageText.value.trim();
  sending.value = true;
  
  try {
    // Stop typing indicator
    realtimeMessagingService.sendTypingIndicator(props.conversationId, false);
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value);
      typingTimeout.value = null;
    }
    
    // Emit sending event
    emit('message-sending', { content: messageContent });
    
    // Clear the input
    messageText.value = '';
    if (messageInput.value) {
      messageInput.value.style.height = 'auto';
    }
    
    // Emit message sent event
    emit('message-sent', { content: messageContent });
    
    // Focus back to input
    await nextTick();
    messageInput.value?.focus();
    
  } catch (error) {
    console.error('Error sending message:', error);
  } finally {
    sending.value = false;
  }
};

// Expose methods for parent component
defineExpose({
  clearMessage,
  focus: () => messageInput.value?.focus()
});

// Cleanup typing timeout on unmount
onUnmounted(() => {
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }
});
</script>

<style scoped>
/* Custom focus styles */
textarea:focus {
  outline: none;
}
</style> 